import React from "react";
import { useLang } from "../lib/i18n";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Plus, Trash2, ListChecks, AlertTriangle } from "lucide-react";

const sel = "h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700";
const CRIT = { high: "Élevée", medium: "Moyenne", low: "Faible" };
const CRIT_CLS = { high: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-800", low: "bg-slate-100 text-slate-600" };

export function ActivitiesTable({ activities, categories, onChange }) {
  const { t } = useLang();
  const set = (i, k, v) => onChange(activities.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  return (
    <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 sm:p-5" data-testid="entry-activities">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><ListChecks className="h-4 w-4 text-[#F05A28]" />{t("Activités réalisées")}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{t("Une ligne par activité : catégorie, résultat ou livrable, participants si formation.")}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...activities, { category: "coordination", description: "", location: "", participants_f: "", participants_m: "", deliverable: "" }])} data-testid="activities-add-btn">
          <Plus className="h-4 w-4 mr-1" />{t("Ajouter")}
        </Button>
      </div>
      <div className="space-y-2">
        {activities.map((a, i) => (
          <div key={i} className="grid grid-cols-2 lg:grid-cols-12 gap-2 items-center rounded-md border border-slate-200 p-2" data-testid={`activity-row-${i}`}>
            <select value={a.category} onChange={(e) => set(i, "category", e.target.value)} className={`${sel} lg:col-span-2`} data-testid={`activity-cat-${i}`}>
              {categories.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
            <Input value={a.description} onChange={(e) => set(i, "description", e.target.value)} placeholder={t("Activité et résultat")} className="h-10 col-span-2 lg:col-span-4" data-testid={`activity-desc-${i}`} />
            <Input value={a.location || ""} onChange={(e) => set(i, "location", e.target.value)} placeholder={t("Lieu (ZS / AS)")} className="h-10 lg:col-span-2" data-testid={`activity-loc-${i}`} />
            {a.category === "training" || a.category === "community" ? (
              <>
                <Input type="number" min="0" value={a.participants_f ?? ""} onChange={(e) => set(i, "participants_f", e.target.value)} placeholder="F" className="h-10" data-testid={`activity-pf-${i}`} />
                <Input type="number" min="0" value={a.participants_m ?? ""} onChange={(e) => set(i, "participants_m", e.target.value)} placeholder="H" className="h-10" data-testid={`activity-pm-${i}`} />
              </>
            ) : (
              <Input value={a.deliverable || ""} onChange={(e) => set(i, "deliverable", e.target.value)} placeholder={t("Livrable")} className="h-10 col-span-2 lg:col-span-2" data-testid={`activity-deliv-${i}`} />
            )}
            <button type="button" onClick={() => onChange(activities.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600 justify-self-end lg:col-span-2 lg:col-start-12" data-testid={`activity-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ChallengesTable({ challenges, onChange }) {
  const { t } = useLang();
  const set = (i, k, v) => onChange(challenges.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  return (
    <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 sm:p-5" data-testid="entry-challenges">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[#F05A28]" />{t("Défis et contraintes")}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{t("Constat, cause racine, impact sur la riposte et criticité. Les actions correctives se formulent dans « Recommandations ».")}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...challenges, { issue: "", root_cause: "", impact: "", criticality: "medium" }])} data-testid="challenges-add-btn">
          <Plus className="h-4 w-4 mr-1" />{t("Ajouter")}
        </Button>
      </div>
      <div className="space-y-2">
        {challenges.map((c, i) => (
          <div key={i} className="grid grid-cols-2 lg:grid-cols-12 gap-2 items-center rounded-md border border-slate-200 p-2" data-testid={`challenge-row-${i}`}>
            <Input value={c.issue} onChange={(e) => set(i, "issue", e.target.value)} placeholder={t("Constat / dysfonctionnement")} className="h-10 col-span-2 lg:col-span-4" data-testid={`challenge-issue-${i}`} />
            <Input value={c.root_cause || ""} onChange={(e) => set(i, "root_cause", e.target.value)} placeholder={t("Cause racine")} className="h-10 lg:col-span-3" data-testid={`challenge-cause-${i}`} />
            <Input value={c.impact || ""} onChange={(e) => set(i, "impact", e.target.value)} placeholder={t("Impact sur la riposte")} className="h-10 lg:col-span-3" data-testid={`challenge-impact-${i}`} />
            <select value={c.criticality} onChange={(e) => set(i, "criticality", e.target.value)} className={sel} data-testid={`challenge-crit-${i}`}>
              {Object.entries(CRIT).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
            </select>
            <button type="button" onClick={() => onChange(challenges.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600 justify-self-end" data-testid={`challenge-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ListsReport({ activities, challenges, categories }) {
  const { t } = useLang();
  const cat = Object.fromEntries((categories || []).map((c) => [c.code, c.label]));
  if (!activities?.length && !challenges?.length) return null;
  return (
    <>
      {activities?.length > 0 && (
        <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 sm:p-5 mb-4" data-testid="gv-activities">
          <h2 className="text-sm font-semibold text-slate-900 mb-3">{t("Activités réalisées")}</h2>
          <ul className="space-y-1.5 text-sm">
            {activities.map((a, i) => (
              <li key={i} className="flex flex-wrap gap-2 items-baseline">
                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFF3EE] text-[#F05A28]">{cat[a.category] || a.category}</span>
                <span className="text-slate-800">{a.description}</span>
                {a.location && <span className="text-xs text-slate-500">· {a.location}</span>}
                {(a.participants_f !== "" || a.participants_m !== "") && (a.participants_f || a.participants_m) && <span className="text-xs text-slate-500">· {Number(a.participants_f || 0) + Number(a.participants_m || 0)} {t("participants")} ({a.participants_f || 0} F / {a.participants_m || 0} H)</span>}
                {a.deliverable && <span className="text-xs text-slate-500">· {a.deliverable}</span>}
                <span className="text-xs text-slate-400">{a.date}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {challenges?.length > 0 && (
        <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 sm:p-5 mb-4" data-testid="gv-challenges">
          <h2 className="text-sm font-semibold text-slate-900 mb-3">{t("Défis et contraintes")}</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b"><th className="py-2 pr-3">{t("Constat")}</th><th className="py-2 pr-3">{t("Cause racine")}</th><th className="py-2 pr-3">{t("Impact")}</th><th className="py-2">{t("Criticité")}</th></tr></thead>
            <tbody>
              {challenges.map((c, i) => (
                <tr key={i} className="border-b border-slate-100 align-top">
                  <td className="py-2 pr-3 text-slate-800">{c.issue}<div className="text-[11px] text-slate-400">{c.date}</div></td>
                  <td className="py-2 pr-3 text-slate-600">{c.root_cause}</td>
                  <td className="py-2 pr-3 text-slate-600">{c.impact}</td>
                  <td className="py-2"><span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${CRIT_CLS[c.criticality]}`}>{t(CRIT[c.criticality])}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
